import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { apiRequest } from "../utils/http";

const formatCurrency = (value) => `NGN ${Number(value || 0).toLocaleString("en-NG")}`;
const brandLogoPath = `${process.env.PUBLIC_URL || ""}/logo.png`;

const QuotationsPage = () => {
  const [quotations, setQuotations] = useState([]);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [confirmingId, setConfirmingId] = useState("");

  const loadQuotations = async (searchTerm = "") => {
    try {
      setError("");
      const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const response = await apiRequest(`/api/quotations${query}`);
      setQuotations(response?.data || []);
    } catch (loadError) {
      setError(loadError.message || "Unable to load quotations.");
    }
  };

  useEffect(() => {
    loadQuotations();
  }, []);

  const totalValue = useMemo(
    () => quotations.reduce((sum, quotation) => sum + Number(quotation.subtotal || 0), 0),
    [quotations]
  );

  const handlePrintQuotation = (quotation) => {
    const printWindow = window.open("", "_blank", "width=900,height=700");

    if (!printWindow) {
      setError("Pop-up blocked. Please allow pop-ups to print the quotation.");
      return;
    }

    const render = async () => {
      const logoDataUrl = await fetch(brandLogoPath)
        .then((response) => response.blob())
        .then(
          (blob) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            })
        )
        .catch(() => null);

    const itemsRows = (quotation.items || [])
      .map(
        (item) => `
          <tr>
            <td>${item.title}</td>
            <td>${item.quantity}</td>
            <td>${formatCurrency(item.unitPrice)}</td>
            <td>${formatCurrency(item.lineTotal)}</td>
          </tr>
        `
      )
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>${quotation.quoteNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px 32px 64px; color: #111827; position: relative; min-height: 100vh; box-sizing: border-box; }
            h1, h2, h3, p { margin: 0 0 12px; }
            .muted { color: #6b7280; }
            .section { margin-top: 28px; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #e5e7eb; padding: 12px; text-align: left; }
            th { background: #f8fafc; }
            .summary { margin-top: 20px; display: flex; justify-content: flex-end; }
            .summary-box { min-width: 260px; }
            .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
            .brand-header, .brand-footer { text-align: center; }
            .brand-header img { width: 120px; height: auto; display: block; margin: 0 auto 14px; }
            .brand-footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #e5e7eb; color: #6b7280; font-size: 12px; }
            .brand-footer img { width: 74px; height: auto; display: block; margin: 0 auto 8px; }
          </style>
        </head>
        <body>
          <div class="brand-header">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Gentle Rentals logo" />` : ""}
            <h1>Gentle Rentals Quotation</h1>
            <p class="muted">${quotation.quoteNumber}</p>
          </div>

          <div class="section">
            <h3>Customer</h3>
            <p>${quotation.customer?.fullName || "Customer"}</p>
            <p class="muted">${quotation.customer?.email || ""}</p>
            <p class="muted">${quotation.customer?.phone || ""}</p>
          </div>

          <div class="section">
            <h3>Event Details</h3>
            <p><strong>Date:</strong> ${quotation.eventDate || "Not set"}</p>
            <p><strong>Rental days:</strong> ${quotation.rentalDays || 1}</p>
            <p><strong>Return date:</strong> ${quotation.returnDate || "Not set"}</p>
            <p><strong>Location:</strong> ${quotation.eventLocation || "Not provided"}</p>
            <p><strong>Emergency contact:</strong> ${quotation.emergencyContactName || "Not provided"}</p>
            <p><strong>Emergency phone:</strong> ${quotation.emergencyContactPhone || "Not provided"}</p>
            <p><strong>Status:</strong> ${quotation.status || "pending"}</p>
            <p><strong>Notes:</strong> ${quotation.notes || "No additional notes"}</p>
          </div>

          <div class="section">
            <h3>Items</h3>
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Qty</th>
                  <th>Unit Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${itemsRows}
              </tbody>
            </table>
          </div>

          <div class="summary">
            <div class="summary-box">
              <div class="summary-row"><span>Total items</span><strong>${quotation.totalItems || 0}</strong></div>
              <div class="summary-row"><span>Subtotal</span><strong>${formatCurrency(quotation.subtotal)}</strong></div>
            </div>
          </div>

          <div class="brand-footer">
            ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Gentle Rentals logo" />` : ""}
            <div>Gentle Events</div>
            <div>${quotation.quoteNumber}</div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    };

    render().catch(() => {
      setError("Unable to render quotation PDF branding.");
    });
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    const nextSearch = search.trim();
    setAppliedSearch(nextSearch);
    await loadQuotations(nextSearch);
  };

  const handleConfirmPayment = async (quotation) => {
    const confirmed = window.confirm(`Confirm payment for ${quotation.quoteNumber}? This will record the sale and update product quantity.`);

    if (!confirmed) {
      return;
    }

    try {
      setError("");
      setSuccess("");
      setConfirmingId(quotation._id);
      const response = await apiRequest(`/api/quotations/${quotation._id}/confirm-payment`, {
        method: "PATCH"
      });
      setSuccess(response?.message || "Payment confirmed successfully.");
      await loadQuotations(appliedSearch);

      if (selectedQuotation?._id === quotation._id) {
        setSelectedQuotation(response?.data || null);
      }
    } catch (confirmError) {
      setError(confirmError.message || "Unable to confirm payment.");
    } finally {
      setConfirmingId("");
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h4" sx={{ mb: 1 }}>Quotation Requests</Typography>
        <Typography sx={{ color: "text.secondary" }}>
          Track customer quote generation, open each quotation, inspect its items, and print it as PDF.
        </Typography>
      </Box>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h5">{quotations.length}</Typography>
          <Typography sx={{ color: "text.secondary" }}>Quotation requests</Typography>
        </Paper>
        <Paper sx={{ p: 3, flex: 1 }}>
          <Typography variant="h5">{formatCurrency(totalValue)}</Typography>
          <Typography sx={{ color: "text.secondary" }}>Combined quotation value</Typography>
        </Paper>
      </Stack>

      <Paper sx={{ p: 2.5 }}>
        <Box component="form" onSubmit={handleSearch}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              label="Search quotations"
              placeholder="Quotation ID, customer email, or phone number"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <Button type="submit" variant="contained" startIcon={<SearchRoundedIcon />} sx={{ minWidth: 180 }}>
              Search
            </Button>
          </Stack>
        </Box>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {appliedSearch && (
        <Typography sx={{ color: "text.secondary" }}>
          Showing results for "{appliedSearch}"
        </Typography>
      )}

      <Paper sx={{ overflow: "hidden" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Quote No.</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Event Date</TableCell>
              <TableCell>Rental Days</TableCell>
              <TableCell>Return Date</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotations.map((quotation) => (
              <TableRow key={quotation._id} hover>
                <TableCell>{quotation.quoteNumber || "Quotation"}</TableCell>
                <TableCell>{quotation.customer?.fullName || "Customer"}</TableCell>
                <TableCell>{quotation.eventDate ? new Date(quotation.eventDate).toLocaleDateString() : "Not set"}</TableCell>
                <TableCell>{quotation.rentalDays || 1}</TableCell>
                <TableCell>{quotation.returnDate ? new Date(quotation.returnDate).toLocaleDateString() : "Not set"}</TableCell>
                <TableCell>{quotation.eventLocation || "Not provided"}</TableCell>
                <TableCell>{quotation.items?.length || 0}</TableCell>
                <TableCell>
                  <Chip label={quotation.status || "pending"} color="warning" sx={{ textTransform: "capitalize" }} />
                </TableCell>
                <TableCell>
                  <Chip
                    label={quotation.paymentStatus || "pending"}
                    color={quotation.paymentStatus === "confirmed" ? "success" : "default"}
                    sx={{ textTransform: "capitalize" }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityRoundedIcon />}
                      onClick={() => setSelectedQuotation(quotation)}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PictureAsPdfRoundedIcon />}
                      onClick={() => handlePrintQuotation(quotation)}
                    >
                      Print PDF
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleRoundedIcon />}
                      disabled={quotation.paymentStatus === "confirmed" || confirmingId === quotation._id}
                      onClick={() => handleConfirmPayment(quotation)}
                    >
                      {confirmingId === quotation._id ? "Confirming..." : "Confirm Payment"}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {quotations.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <Typography sx={{ py: 2, color: "text.secondary" }}>No quotations available yet.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={Boolean(selectedQuotation)} onClose={() => setSelectedQuotation(null)} fullWidth maxWidth="md">
        <DialogTitle>Quotation Details</DialogTitle>
        <DialogContent>
          {selectedQuotation && (
            <Stack spacing={2.5} sx={{ pt: 1 }}>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="h6">{selectedQuotation.quoteNumber}</Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {selectedQuotation.customer?.fullName || "Customer"}
                  </Typography>
                  <Typography sx={{ color: "text.secondary" }}>
                    {selectedQuotation.customer?.email || ""}
                  </Typography>
                </Box>
                <Chip
                  label={selectedQuotation.status || "pending"}
                  color="warning"
                  sx={{ textTransform: "capitalize", width: "fit-content" }}
                />
              </Stack>

              <Divider />

              <Stack spacing={1}>
                <Typography><strong>Event date:</strong> {selectedQuotation.eventDate || "Not set"}</Typography>
                <Typography><strong>Rental days:</strong> {selectedQuotation.rentalDays || 1}</Typography>
                <Typography><strong>Return date:</strong> {selectedQuotation.returnDate || "Not set"}</Typography>
                <Typography><strong>Event location:</strong> {selectedQuotation.eventLocation || "Not provided"}</Typography>
                <Typography><strong>Emergency contact:</strong> {selectedQuotation.emergencyContactName || "Not provided"}</Typography>
                <Typography><strong>Emergency phone:</strong> {selectedQuotation.emergencyContactPhone || "Not provided"}</Typography>
                <Typography><strong>Customer phone:</strong> {selectedQuotation.customer?.phone || "Not provided"}</Typography>
                <Typography><strong>Payment status:</strong> {selectedQuotation.paymentStatus || "pending"}</Typography>
                <Typography>
                  <strong>Payment confirmed at:</strong>{" "}
                  {selectedQuotation.paymentConfirmedAt
                    ? new Date(selectedQuotation.paymentConfirmedAt).toLocaleString()
                    : "Not confirmed"}
                </Typography>
                <Typography>
                  <strong>Confirmed by:</strong>{" "}
                  {selectedQuotation.paymentConfirmedBy?.fullName || selectedQuotation.paymentConfirmedBy?.email || "Not yet assigned"}
                </Typography>
                <Typography><strong>Notes:</strong> {selectedQuotation.notes || "No additional notes"}</Typography>
              </Stack>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Items</Typography>
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(selectedQuotation.items || []).map((item, index) => (
                        <TableRow key={`${item.slug}-${index}`}>
                          <TableCell>{item.title}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell>{formatCurrency(item.lineTotal)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                <Typography><strong>Total items:</strong> {selectedQuotation.totalItems || 0}</Typography>
                <Typography variant="h6"><strong>Subtotal:</strong> {formatCurrency(selectedQuotation.subtotal)}</Typography>
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedQuotation && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: "100%", justifyContent: "space-between" }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleRoundedIcon />}
                disabled={selectedQuotation.paymentStatus === "confirmed" || confirmingId === selectedQuotation._id}
                onClick={() => handleConfirmPayment(selectedQuotation)}
              >
                {confirmingId === selectedQuotation._id ? "Confirming..." : "Confirm Payment"}
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdfRoundedIcon />}
                onClick={() => handlePrintQuotation(selectedQuotation)}
              >
                Print PDF
              </Button>
            </Stack>
          )}
          <Button onClick={() => setSelectedQuotation(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default QuotationsPage;
